import cleanGatewayResponse from "./cleanGatewayResponse";

/**
 * Reads chunks from a ReadableStream and processes them using a callback function.
 *
 * @param {ReadableStream} stream - The ReadableStream to read data from.
 * @param {(token: string, isLastChunk: boolean) => void} onNewChunk - Callback function that receives each chunk as it arrives
 *        and a boolean indicating if this is the last chunk.
 *
 * @return {Promise<string>} - A string that concatenates all the chunks read from the stream.
 */
export default async function readMessageStream(
  stream: ReadableStream,
  onNewChunk: (token: string, isLastChunk?: boolean) => void = () => {}, // callback function gets chunks as they arrive
): Promise<{ chunks: string; citations: string[] }> {
  if (!stream) {
    console.error("Stream is undefined or null");
    return { chunks: "", citations: [] };
  }

  const reader = stream.getReader();
  const chunks: string[] = [];
  let citations: string[] = [];
  let hasError = false;
  let bytesRead = 0;

  try {
    console.log("Starting to read message stream");
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log(`Stream complete, read ${bytesRead} bytes and ${chunks.length} chunks`);
        // Call onNewChunk with empty string and isLastChunk=true to signal completion
        onNewChunk("", true);
        break;
      }
      
      if (value) {
        bytesRead += value.length;
        
        try {
          const chunk = cleanGatewayResponse(value);
          if (chunk.chunk) {
            chunks.push(chunk.chunk);
            onNewChunk(chunk.chunk, false);
            console.log(`Processed chunk: ${chunk.chunk.length} chars`);
          }
          if (chunk.citations && chunk.citations.length > 0) {
            citations = chunk.citations;
          }
        } catch (chunkError) {
          console.error("Error processing chunk:", chunkError);
          // Continue reading even if this chunk failed
        }
      }
    }
  } catch (error) {
    hasError = true;
    console.error("Error reading stream:", error);
    // Notify callback of error and completion
    onNewChunk("Error reading stream", true);
  } finally {
    try {
      // Always release the reader
      reader.releaseLock();
      console.log("Stream reader released");
    } catch (error) {
      console.warn("Error releasing stream reader:", error);
    }
  }

  // If we encountered an error but have some content, return what we have
  if (hasError && chunks.length === 0) {
    return { 
      chunks: "Error: Could not retrieve response from the language model.",
      citations: []
    };
  }

  const fullContent = chunks.join("");
  console.log(`Stream fully processed, total content: ${fullContent.length} chars`);
  return { chunks: fullContent, citations: citations };
}
